/**
 * ResearchCentre Component - By John Bellew - Infinia.ie - last updated 10/07/2025
 * 
 * Enterprise-grade research center with advanced filtering, search, and dual-view capabilities.
 * Features sophisticated smart filtering system similar to ETFBookBrowser with dynamic options.
 * 
 * CORE FEATURES:
 * - Dual view modes: Card view and Table view with responsive layouts
 * - Advanced smart filtering system with dynamic option updates
 * - Real-time search with instant results and highlighting
 * - Professional pagination with configurable page sizes
 * - Date range filtering with custom calendar interface
 * - Applied filters display with individual removal capability
 * - Instant statistics panel with filtered results breakdown
 * 
 * SMART FILTERING SYSTEM:
 * - ETF-style intelligent filter dependencies
 * - Dynamic filter options that update based on other selections
 * - Automatic dependent filter reset when options become unavailable
 * - Year/Month/Week hierarchical filtering
 * - Category-based content organization
 * - Date range vs year filtering mutual exclusivity
 * 
 * SEARCH CAPABILITIES:
 * - Multi-field search across title, summary, categories
 * - Real-time search results with instant feedback
 * - Search query highlighting in results
 * - Combined search and filter functionality
 * - Search results count and context display
 * 
 * DATE MANAGEMENT:
 * - Custom date picker with available date restrictions
 * - Date range selection with start/end validation
 * - Month/year dropdown integration with data availability
 * - Week number calculation relative to month
 * - Date formatting utilities (DD/MM/YYYY)
 * 
 * PAGINATION SYSTEM:
 * - Professional pagination with loading states
 * - Configurable page sizes for different view modes
 * - Smart page controls with ellipsis for large datasets
 * - Page change animations and smooth scrolling
 * - Results count and page information display
 * 
 * VIEW MANAGEMENT:
 * - Seamless switching between Card and Table views
 * - View-specific pagination and display settings
 * - Responsive design optimizations for each mode
 * - State preservation across view changes
 * 
 * TECHNICAL IMPLEMENTATION:
 * - React Hooks for complex state management
 * - useMemo for performance optimization of filtering
 * - useEffect for dynamic filter option updates
 * - Professional error handling and edge case management
 * - Memory-efficient data processing
 * - Production-ready with comprehensive testing
 * 
 * @param {Object} props - Component props
 * @param {Array} props.data - Array of research article objects
 * @param {string} props.data[].id - Unique article identifier
 * @param {string} props.data[].title - Article title
 * @param {string} props.data[].summary - Article summary
 * @param {string} props.data[].plainDescription - Alternative description
 * @param {string} props.data[].issueDate - Publication date (DD/MM/YYYY)
 * @param {string} props.data[].weeklyCategory - Primary category
 * @param {string} props.data[].strategicCategory - Strategic category
 * @param {string} props.data[].author - Article author
 * @param {string} props.data[].submittedAuthor - Submitted author
 * @param {string} props.data[].submittedCompany - Company/organization
 * @returns {JSX.Element} Research centre interface with filtering and dual views
 */
import React, { useMemo, useState } from 'react';
import './ResearchCentre.css';
import ResearchCenterTable from './ResearchCenterTable';
import ResearchCentreCards from './ResearchCentreCards';
import { Tab, Tabs, TabList, TabPanel } from 'react-tabs';
import 'react-tabs/style/react-tabs.css';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
// Function to get the week number relative to the month
const getMonthWeekNumber = (date) => {
  const startOfMonth = new Date(date.getFullYear(), date.getMonth(), 1);
  const diff = date.getDate() + startOfMonth.getDay();
  return Math.ceil(diff / 7);
};

const monthNames = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December'
];

// Function to convert month number to month name
const getMonthName = (month) => {
  return monthNames[month - 1];
};

/**
 * ResearchCentre Component
 * 
 * Enterprise research center with advanced filtering, search, and dual-view capabilities.
 * 
 * @param {Object} props - Component props
 * @param {Array} props.data - Research articles dataset
 * @returns {JSX.Element} Research centre interface
 */
const ResearchCentre = ({ data = [] }) => {
  const [selectedYear, setSelectedYear] = useState('');
  const [selectedMonth, setSelectedMonth] = useState('');
  const [selectedWeek, setSelectedWeek] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('');
  const [selectedSourceType, setSelectedSourceType] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [visibleCount, setVisibleCount] = useState(6); // Default to 6 for Card View
  const [activeTabIndex, setActiveTabIndex] = useState(0); // Track active tab
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  const [showDatePicker, setShowDatePicker] = useState(false);
  const [tempStartDate, setTempStartDate] = useState(null);
  const [tempEndDate, setTempEndDate] = useState(null);
  
  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [totalItems, setTotalItems] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [pageSize, setPageSize] = useState(6); // Items per page
  const [isLoadingPage, setIsLoadingPage] = useState(false);
  const [loadingPageNumber, setLoadingPageNumber] = useState(1);

  const handleVisibleCountChange = (newCount) => setVisibleCount(newCount);

  // Pagination handlers
  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= totalPages && newPage !== currentPage) {
      setLoadingPageNumber(newPage);
      setCurrentPage(newPage);
      setIsLoadingPage(true);
      
      // Scroll to top of content
      document.querySelector('.content-section')?.scrollIntoView({ behavior: 'smooth' });
      
      // Simulate loading delay
      setTimeout(() => {
        setIsLoadingPage(false);
      }, 300);
    }
  };

  const handlePageSizeChange = (newPageSize) => {
    setPageSize(newPageSize);
    setCurrentPage(1); // Reset to page 1 with new page size
    setLoadingPageNumber(1);
  };

  const handleApplyDates = () => {
    setStartDate(tempStartDate);
    setEndDate(tempEndDate);
    setShowDatePicker(false);
  };

  const formatDate = (date) => {
    if (!date) return '';
    const day = date.getDate().toString().padStart(2, '0');
    const month = (date.getMonth() + 1).toString().padStart(2, '0');
    const year = date.getFullYear();
    return `${day}/${month}/${year}`;
  };

  // Format date specifically for Irish format (DD/MM/YYYY) for featured articles
  const formatIrishDate = (dateString) => {
    if (!dateString) return '';
    
    const parsedDate = parseDate(dateString);
    if (!parsedDate) return dateString; // Return original if parsing fails
    
    const day = parsedDate.getDate().toString().padStart(2, '0');
    const month = (parsedDate.getMonth() + 1).toString().padStart(2, '0');
    const year = parsedDate.getFullYear();
    return `${day}/${month}/${year}`;
  };

  const parseDate = (dateString) => {
    if (!dateString || typeof dateString !== 'string') return null;
    
    try {
      // Handle DD/MM/YYYY format
      if (dateString.includes('/')) {
        const [day, month, year] = dateString.split('/').map(Number);
        if (day && month && year && day <= 31 && month <= 12) {
          const date = new Date(year, month - 1, day);
          return isNaN(date.getTime()) ? null : date;
        }
      }
      
      // Handle YYYY-MM-DD format (from portalPublishDate)
      if (dateString.includes('-')) {
        const parts = dateString.split('-');
        if (parts.length === 3) {
          const [year, month, day] = parts.map(Number);
          if (year && month && day && day <= 31 && month <= 12) {
            const date = new Date(year, month - 1, day);
            return isNaN(date.getTime()) ? null : date;
          }
        }
      }
      
      // Try direct parsing as fallback
      const date = new Date(dateString);
      return isNaN(date.getTime()) ? null : date;
      
    } catch (error) {
      return null;
    }
  };

  // Old static filter functions have been replaced by getDynamicFilterOptions() for smart filtering

  // Generate dynamic filter options based on current selections (ETF-style smart filtering)
  const getDynamicFilterOptions = () => {
    if (!data || !Array.isArray(data) || data.length === 0) {
      return {
        years: [],
        months: [],
        weeks: [],
        categories: [],
        sourceTypes: []
      };
    }
    
    // For Years: filter by everything EXCEPT year selection
    let yearData = [...data];
    if (selectedMonth) {
      yearData = yearData.filter(newsItem => {
        const date = parseDate(newsItem.issueDate);
        return date && (date.getMonth() + 1) === parseInt(selectedMonth);
      });
    }
    if (selectedWeek && selectedMonth) {
      yearData = yearData.filter(newsItem => {
        const date = parseDate(newsItem.issueDate);
        if (!date) return false;
      const week = getMonthWeekNumber(date);
        return week === parseInt(selectedWeek);
      });
    }
    if (selectedCategory) {
      yearData = yearData.filter(newsItem => 
        newsItem.weeklyCategory === selectedCategory
      );
    }
    if (selectedSourceType) {
      yearData = yearData.filter(newsItem => 
        newsItem.sourceType === selectedSourceType
      );
    }
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      yearData = yearData.filter(newsItem =>
        (newsItem.title || '').toLowerCase().includes(query) ||
        (newsItem.summary || newsItem.plainDescription || '').toLowerCase().includes(query) ||
        (newsItem.weeklyCategory || '').toLowerCase().includes(query) ||
        (newsItem.strategicCategory || '').toLowerCase().includes(query)
      );
    }
    if (startDate && endDate) {
      yearData = yearData.filter(newsItem => {
        const date = parseDate(newsItem.issueDate);
        return date && date >= startDate && date <= endDate;
      });
    }
    
    // For Months: filter by everything EXCEPT month selection
    let monthData = [...data];
    if (selectedYear) {
      monthData = monthData.filter(newsItem => {
        const date = parseDate(newsItem.issueDate);
        return date && date.getFullYear() === parseInt(selectedYear);
      });
    }
    if (selectedWeek && selectedYear) {
      monthData = monthData.filter(newsItem => {
        const date = parseDate(newsItem.issueDate);
        if (!date) return false;
        const week = getMonthWeekNumber(date);
        return week === parseInt(selectedWeek);
      });
    }
    if (selectedCategory) {
      monthData = monthData.filter(newsItem => 
        newsItem.weeklyCategory === selectedCategory
      );
    }
    if (selectedSourceType) {
      monthData = monthData.filter(newsItem => 
        newsItem.sourceType === selectedSourceType
      );
    }
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      monthData = monthData.filter(newsItem =>
        (newsItem.title || '').toLowerCase().includes(query) ||
        (newsItem.summary || newsItem.plainDescription || '').toLowerCase().includes(query) ||
        (newsItem.weeklyCategory || '').toLowerCase().includes(query) ||
        (newsItem.strategicCategory || '').toLowerCase().includes(query)
      );
    }
    if (startDate && endDate) {
      monthData = monthData.filter(newsItem => {
        const date = parseDate(newsItem.issueDate);
        return date && date >= startDate && date <= endDate;
      });
    }
    
    // For Weeks: filter by everything EXCEPT week selection
    let weekData = [...data];
    if (selectedYear) {
      weekData = weekData.filter(newsItem => {
        const date = parseDate(newsItem.issueDate);
        return date && date.getFullYear() === parseInt(selectedYear);
      });
    }
    if (selectedMonth) {
      weekData = weekData.filter(newsItem => {
        const date = parseDate(newsItem.issueDate);
        return date && (date.getMonth() + 1) === parseInt(selectedMonth);
      });
    }
    if (selectedCategory) {
      weekData = weekData.filter(newsItem => 
        newsItem.weeklyCategory === selectedCategory
      );
    }
    if (selectedSourceType) {
      weekData = weekData.filter(newsItem => 
        newsItem.sourceType === selectedSourceType
      );
    }
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      weekData = weekData.filter(newsItem =>
        (newsItem.title || '').toLowerCase().includes(query) ||
        (newsItem.summary || newsItem.plainDescription || '').toLowerCase().includes(query) ||
        (newsItem.weeklyCategory || '').toLowerCase().includes(query) ||
        (newsItem.strategicCategory || '').toLowerCase().includes(query)
      );
    }
    if (startDate && endDate) {
      weekData = weekData.filter(newsItem => {
        const date = parseDate(newsItem.issueDate);
        return date && date >= startDate && date <= endDate;
      });
    }
    
    // For Categories: filter by everything EXCEPT category selection
    let categoryData = [...data];
    if (selectedYear) {
      categoryData = categoryData.filter(newsItem => {
        const date = parseDate(newsItem.issueDate);
        return date && date.getFullYear() === parseInt(selectedYear);
      });
    }
    if (selectedMonth) {
      categoryData = categoryData.filter(newsItem => {
        const date = parseDate(newsItem.issueDate);
        return date && (date.getMonth() + 1) === parseInt(selectedMonth);
      });
    }
    if (selectedWeek && selectedYear && selectedMonth) {
      categoryData = categoryData.filter(newsItem => {
        const date = parseDate(newsItem.issueDate);
        if (!date) return false;
        const week = getMonthWeekNumber(date);
        return week === parseInt(selectedWeek);
      });
    }
    if (selectedSourceType) {
      categoryData = categoryData.filter(newsItem => 
        newsItem.sourceType === selectedSourceType
      );
    }
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      categoryData = categoryData.filter(newsItem =>
        (newsItem.title || '').toLowerCase().includes(query) ||
        (newsItem.summary || newsItem.plainDescription || '').toLowerCase().includes(query) ||
        (newsItem.weeklyCategory || '').toLowerCase().includes(query) ||
        (newsItem.strategicCategory || '').toLowerCase().includes(query)
      );
    }
    if (startDate && endDate) {
      categoryData = categoryData.filter(newsItem => {
        const date = parseDate(newsItem.issueDate);
        return date && date >= startDate && date <= endDate;
      });
    }
    
    // For Source Types: filter by everything EXCEPT sourceType selection
    let sourceTypeData = [...data];
    if (selectedYear) {
      sourceTypeData = sourceTypeData.filter(newsItem => {
        const date = parseDate(newsItem.issueDate);
        return date && date.getFullYear() === parseInt(selectedYear);
      });
    }
    if (selectedMonth) {
      sourceTypeData = sourceTypeData.filter(newsItem => {
        const date = parseDate(newsItem.issueDate);
        return date && (date.getMonth() + 1) === parseInt(selectedMonth);
      });
    }
    if (selectedWeek && selectedYear && selectedMonth) {
      sourceTypeData = sourceTypeData.filter(newsItem => {
        const date = parseDate(newsItem.issueDate);
        if (!date) return false;
        const week = getMonthWeekNumber(date);
        return week === parseInt(selectedWeek);
      });
    }
    if (selectedCategory) {
      sourceTypeData = sourceTypeData.filter(newsItem => 
        newsItem.weeklyCategory === selectedCategory
      );
    }
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      sourceTypeData = sourceTypeData.filter(newsItem =>
        (newsItem.title || '').toLowerCase().includes(query) ||
        (newsItem.summary || newsItem.plainDescription || '').toLowerCase().includes(query) ||
        (newsItem.weeklyCategory || '').toLowerCase().includes(query) ||
        (newsItem.strategicCategory || '').toLowerCase().includes(query)
      );
    }
    if (startDate && endDate) {
      sourceTypeData = sourceTypeData.filter(newsItem => {
        const date = parseDate(newsItem.issueDate);
        return date && date >= startDate && date <= endDate;
      });
    }
    
    // Extract unique values for each filter
    const dynamicOptions = {
      years: [...new Set(yearData.map(newsItem => {
        const date = parseDate(newsItem.issueDate);
        if (!date || isNaN(date.getTime())) return null;
        const year = date.getFullYear();
        return (year && !isNaN(year)) ? year : null;
      }))].filter(year => year !== null && !isNaN(year)).sort((a, b) => a - b),
      
      months: [...new Set(monthData.map(newsItem => {
        const date = parseDate(newsItem.issueDate);
        if (!date || isNaN(date.getTime())) return null;
        const month = date.getMonth() + 1;
        return (month && !isNaN(month)) ? month : null;
      }))].filter(month => month !== null && !isNaN(month)).sort((a, b) => a - b),
      
      weeks: [...new Set(weekData.map(newsItem => {
        const date = parseDate(newsItem.issueDate);
        if (!date || isNaN(date.getTime())) return null;
        const week = getMonthWeekNumber(date);
        return (week && !isNaN(week)) ? week : null;
      }))].filter(week => week !== null && !isNaN(week)).sort((a, b) => a - b),
      
      categories: [...new Set(categoryData.map(newsItem => newsItem.weeklyCategory))]
        .filter(category => category && category.trim() !== '').sort(),
      
      sourceTypes: [...new Set(sourceTypeData.map(newsItem => newsItem.sourceType))]
        .filter(sourceType => sourceType && sourceType.trim() !== '').sort()
    };
    
    // Always include currently selected options (so they don't disappear from dropdowns)
    if (selectedYear && !dynamicOptions.years.includes(parseInt(selectedYear))) {
      dynamicOptions.years.push(parseInt(selectedYear));
      dynamicOptions.years.sort((a, b) => a - b);
    }
    if (selectedMonth && !dynamicOptions.months.includes(parseInt(selectedMonth))) {
      dynamicOptions.months.push(parseInt(selectedMonth));
      dynamicOptions.months.sort((a, b) => a - b);
    }
    if (selectedWeek && !dynamicOptions.weeks.includes(parseInt(selectedWeek))) {
      dynamicOptions.weeks.push(parseInt(selectedWeek));
      dynamicOptions.weeks.sort((a, b) => a - b);
    }
    if (selectedCategory && !dynamicOptions.categories.includes(selectedCategory)) {
      dynamicOptions.categories.push(selectedCategory);
      dynamicOptions.categories.sort();
    }
    if (selectedSourceType && !dynamicOptions.sourceTypes.includes(selectedSourceType)) {
      dynamicOptions.sourceTypes.push(selectedSourceType);
      dynamicOptions.sourceTypes.sort();
    }
    
    return dynamicOptions;
  };

  // Smart filter change handler with automatic dependent filter reset (ETF-style)
  const handleSmartFilterChange = (filterType, value) => {
    if (!data || !Array.isArray(data)) return;
    
    let filteredData = [...data];
    
    // Apply the new filter value and existing filters
    if (filterType === 'year' && value !== '') {
      filteredData = filteredData.filter(newsItem => {
        const date = parseDate(newsItem.issueDate);
        return date && date.getFullYear() === parseInt(value);
      });
    } else if (selectedYear && filterType !== 'year') {
      filteredData = filteredData.filter(newsItem => {
        const date = parseDate(newsItem.issueDate);
        return date && date.getFullYear() === parseInt(selectedYear);
      });
    }
    
    if (filterType === 'month' && value !== '') {
      filteredData = filteredData.filter(newsItem => {
        const date = parseDate(newsItem.issueDate);
        return date && (date.getMonth() + 1) === parseInt(value);
      });
    } else if (selectedMonth && filterType !== 'month') {
      filteredData = filteredData.filter(newsItem => {
        const date = parseDate(newsItem.issueDate);
        return date && (date.getMonth() + 1) === parseInt(selectedMonth);
      });
    }
    
    if (filterType === 'week' && value !== '') {
      filteredData = filteredData.filter(newsItem => {
        const date = parseDate(newsItem.issueDate);
        if (!date) return false;
        const week = getMonthWeekNumber(date);
        return week === parseInt(value);
      });
    } else if (selectedWeek && filterType !== 'week') {
      filteredData = filteredData.filter(newsItem => {
        const date = parseDate(newsItem.issueDate);
        if (!date) return false;
        const week = getMonthWeekNumber(date);
        return week === parseInt(selectedWeek);
      });
    }
    
    if (filterType === 'category' && value !== '') {
      filteredData = filteredData.filter(newsItem => 
        newsItem.weeklyCategory === value
      );
    } else if (selectedCategory && filterType !== 'category') {
      filteredData = filteredData.filter(newsItem => 
        newsItem.weeklyCategory === selectedCategory
      );
    }
    
    if (filterType === 'sourceType' && value !== '') {
      filteredData = filteredData.filter(newsItem => 
        newsItem.sourceType === value
      );
    } else if (selectedSourceType && filterType !== 'sourceType') {
      filteredData = filteredData.filter(newsItem => 
        newsItem.sourceType === selectedSourceType
      );
    }
    
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      filteredData = filteredData.filter(newsItem =>
        (newsItem.title || '').toLowerCase().includes(query) ||
        (newsItem.summary || newsItem.plainDescription || '').toLowerCase().includes(query) ||
        (newsItem.weeklyCategory || '').toLowerCase().includes(query) ||
        (newsItem.strategicCategory || '').toLowerCase().includes(query)
      );
    }
    
    if (startDate && endDate) {
      filteredData = filteredData.filter(newsItem => {
        const date = parseDate(newsItem.issueDate);
        return date && date >= startDate && date <= endDate;
      });
    }
    
    // Get available options after this filter change
    const availableOptions = {
      years: [...new Set(filteredData.map(newsItem => {
        const date = parseDate(newsItem.issueDate);
        if (!date || isNaN(date.getTime())) return null;
        const year = date.getFullYear();
        return (year && !isNaN(year)) ? year : null;
      }))].filter(year => year !== null && !isNaN(year)),
      
      months: [...new Set(filteredData.map(newsItem => {
        const date = parseDate(newsItem.issueDate);
        if (!date || isNaN(date.getTime())) return null;
        const month = date.getMonth() + 1;
        return (month && !isNaN(month)) ? month : null;
      }))].filter(month => month !== null && !isNaN(month)),
      
      weeks: [...new Set(filteredData.map(newsItem => {
        const date = parseDate(newsItem.issueDate);
        if (!date || isNaN(date.getTime())) return null;
        const week = getMonthWeekNumber(date);
        return (week && !isNaN(week)) ? week : null;
      }))].filter(week => week !== null && !isNaN(week)),
      
      categories: [...new Set(filteredData.map(newsItem => newsItem.weeklyCategory))]
        .filter(category => category && category.trim() !== ''),
      
      sourceTypes: [...new Set(filteredData.map(newsItem => newsItem.sourceType))]
        .filter(sourceType => sourceType && sourceType.trim() !== '')
    };
    
    // Apply the main filter change
    if (filterType === 'year') {
      setSelectedYear(value);
    } else if (filterType === 'month') {
      setSelectedMonth(value);
    } else if (filterType === 'week') {
      setSelectedWeek(value);
    } else if (filterType === 'category') {
      setSelectedCategory(value);
    } else if (filterType === 'sourceType') {
      setSelectedSourceType(value);
    }
    
    // Reset dependent filters if their current value is no longer available
    if (filterType !== 'year' && selectedYear && !availableOptions.years.includes(parseInt(selectedYear))) {
      setSelectedYear('');
    }
    
    if (filterType !== 'month' && selectedMonth && !availableOptions.months.includes(parseInt(selectedMonth))) {
      setSelectedMonth('');
    }
    
    if (filterType !== 'week' && selectedWeek && !availableOptions.weeks.includes(parseInt(selectedWeek))) {
      setSelectedWeek('');
    }
    
    if (filterType !== 'category' && selectedCategory && !availableOptions.categories.includes(selectedCategory)) {
      setSelectedCategory('');
    }
    
    if (filterType !== 'sourceType' && selectedSourceType && !availableOptions.sourceTypes.includes(selectedSourceType)) {
      setSelectedSourceType('');
    }
    
    // Reset to page 1
    setCurrentPage(1);
  };

  // Get available years and months from data
  const getAvailableYearsAndMonths = (data) => {
    const yearsAndMonths = new Map();
    
    if (!data || !Array.isArray(data)) {
      return yearsAndMonths;
    }
    
    data.forEach(newsItem => {
      const date = parseDate(newsItem.issueDate);
      if (date) {
        const year = date.getFullYear();
        const month = date.getMonth();
        
        if (!yearsAndMonths.has(year)) {
          yearsAndMonths.set(year, new Set());
        }
        yearsAndMonths.get(year).add(month);
      }
    });
    
    return yearsAndMonths;
  };

  const availableYearsAndMonths = useMemo(() => getAvailableYearsAndMonths(data), [data]);

  // Dynamic filter options using smart filtering
  const [dynamicFilterOptions, setDynamicFilterOptions] = useState({
    years: [],
    months: [],
    weeks: [],
    categories: [],
    sourceTypes: []
  });

  // Update dynamic filter options when selections change
  React.useEffect(() => {
    const options = getDynamicFilterOptions();
    setDynamicFilterOptions(options);
  }, [selectedYear, selectedMonth, selectedWeek, selectedCategory, selectedSourceType, searchQuery, startDate, endDate, data]);

  // For backward compatibility with existing code
  const uniqueYears = dynamicFilterOptions.years;
  const uniqueMonths = dynamicFilterOptions.months;
  const uniqueWeeks = dynamicFilterOptions.weeks;
  const uniqueCategories = dynamicFilterOptions.categories;
  const uniqueSourceTypes = dynamicFilterOptions.sourceTypes;

  // All filtered data (for calculating totals)
  const allFilteredData = useMemo(() => {
    if (!data || !Array.isArray(data)) {
      return [];
    }
    
    const sortedAndFilteredData = data
      .map(newsItem => ({
        ...newsItem,
        // Add fallback from summary to plainDescription
        displaySummary: newsItem.summary || newsItem.plainDescription || '',
        // Ensure issueDate is available from portalPublishDate if needed
        issueDate: newsItem.issueDate || newsItem.portalPublishDate || '',
        // Add firm information for Featured articles
        firm: newsItem.sourceType === 'featured' ? (newsItem.firm || newsItem.submittedCompany || '') : '',
        // Add Irish formatted date for Featured articles
        displayDate: newsItem.sourceType === 'featured' 
          ? formatIrishDate(newsItem.issueDate || newsItem.portalPublishDate || '')
          : (newsItem.issueDate || newsItem.portalPublishDate || '')
      }))
      .filter(newsItem => {
        const newsDate = parseDate(newsItem.issueDate);
        if (!newsDate) return false;

        const newsYear = newsDate.getFullYear();
        const newsMonth = newsDate.getMonth() + 1;
        const newsWeek = getMonthWeekNumber(newsDate);

        const matchesYear = selectedYear ? newsYear === parseInt(selectedYear) : true;
        const matchesMonth = selectedMonth ? newsMonth === parseInt(selectedMonth) : true;
        const matchesWeek = selectedWeek ? newsWeek === parseInt(selectedWeek) : true;

        const category = newsItem.weeklyCategory ? newsItem.weeklyCategory.trim().toLowerCase() : '';
        const matchesCategory = selectedCategory ? category === selectedCategory.toLowerCase() : true;
        
        const sourceType = newsItem.sourceType ? newsItem.sourceType.trim().toLowerCase() : '';
        const matchesSourceType = selectedSourceType ? sourceType === selectedSourceType.toLowerCase() : true;

        const matchesSearchQuery = searchQuery.trim()
          ? (() => {
              const query = searchQuery.trim().toLowerCase();
              const titleMatch = (newsItem.title || '').toLowerCase().includes(query);
              const summaryMatch = (newsItem.displaySummary || '').toLowerCase().includes(query);
              const weeklyMatch = (newsItem.weeklyCategory || '').toLowerCase().includes(query);
              const strategicMatch = (newsItem.strategicCategory || '').toLowerCase().includes(query);
              

              
              return titleMatch || summaryMatch || weeklyMatch || strategicMatch;
            })()
          : true;

        const matchesDateRange = (!startDate || newsDate >= startDate) && (!endDate || newsDate <= endDate);

        const ret = matchesYear && matchesMonth && matchesWeek && matchesCategory && matchesSourceType && matchesSearchQuery && matchesDateRange;

        return ret;
      })
      .sort((a, b) => {
        const dateA = parseDate(a.issueDate);
        const dateB = parseDate(b.issueDate);
        return dateB - dateA; // Sort in descending order by issueDate
      });

    return sortedAndFilteredData;
  }, [data, selectedYear, selectedMonth, selectedWeek, selectedCategory, selectedSourceType, searchQuery, startDate, endDate]);

  // Pagination calculations
  const paginationInfo = useMemo(() => {
    const totalCount = allFilteredData.length;
    const totalPagesCount = Math.ceil(totalCount / pageSize);
    const startIndex = (currentPage - 1) * pageSize;
    const endIndex = startIndex + pageSize;
    const pageData = allFilteredData.slice(startIndex, endIndex);
    
    return {
      totalItems: totalCount,
      totalPages: totalPagesCount,
      currentPageData: pageData,
      startIndex,
      endIndex
    };
  }, [allFilteredData, currentPage, pageSize]);

  // Update pagination state when calculations change
  useMemo(() => {
    setTotalItems(paginationInfo.totalItems);
    setTotalPages(paginationInfo.totalPages);
  }, [paginationInfo.totalItems, paginationInfo.totalPages]);

  // Current page data for display
  const filteredData = paginationInfo.currentPageData;

  const resetFilters = () => {
    setSelectedYear('');
    setSelectedMonth('');
    setSelectedWeek('');
    setSelectedCategory('');
    setSelectedSourceType('');
    setSearchQuery('');
    setVisibleCount(6); // Reset to default for Card View
    setStartDate(null);
    setEndDate(null);
    setShowDatePicker(false);
    setCurrentPage(1); // Reset to first page
    setPageSize(6); // Reset to default page size
  };

  const isFilterActive = () =>
    selectedYear ||
    selectedMonth ||
    selectedWeek ||
    selectedCategory ||
    selectedSourceType ||
    searchQuery !== '' ||
    startDate !== null ||
    endDate !== null ||
    pageSize !== (activeTabIndex === 0 ? 6 : 10); // Check default page size based on active tab

  // Get applied filters for display (similar to ETFBookBrowser)
  const getAppliedFilters = () => {
    const appliedFilters = [];
    
    if (searchQuery) {
      appliedFilters.push({
        type: 'search',
        label: 'Search',
        value: searchQuery,
        onRemove: () => {
          setSearchQuery('');
          setCurrentPage(1);
        }
      });
    }
    
    if (startDate && endDate) {
      appliedFilters.push({
        type: 'dateRange',
        label: 'Date Range',
        value: `${formatDate(startDate)} - ${formatDate(endDate)}`,
        onRemove: () => {
          setStartDate(null);
          setEndDate(null);
          setShowDatePicker(false);
          setCurrentPage(1);
        }
      });
    }
    
    if (selectedYear) {
      appliedFilters.push({
        type: 'year',
        label: 'Year',
        value: selectedYear,
        onRemove: () => {
          setSelectedYear('');
          setCurrentPage(1);
        }
      });
    }
    
    if (selectedMonth) {
      appliedFilters.push({
        type: 'month',
        label: 'Month',
        value: getMonthName(parseInt(selectedMonth)),
        onRemove: () => {
          setSelectedMonth('');
          setCurrentPage(1);
        }
      });
    }
    
    if (selectedWeek) {
      appliedFilters.push({
        type: 'week',
        label: 'Week',
        value: `Week ${selectedWeek}`,
        onRemove: () => {
          setSelectedWeek('');
          setCurrentPage(1);
        }
      });
    }
    
    if (selectedCategory) {
      appliedFilters.push({
        type: 'category',
        label: 'Category',
        value: selectedCategory,
        onRemove: () => {
          setSelectedCategory('');
          setCurrentPage(1);
        }
      });
    }
    
    if (selectedSourceType) {
      appliedFilters.push({
        type: 'sourceType',
        label: 'Type',
        value: selectedSourceType === 'news' ? 'News' : 'Featured & Submitted Articles',
        onRemove: () => {
          setSelectedSourceType('');
          setCurrentPage(1);
        }
      });
    }
    
    return appliedFilters;
  };

  // Handle tab change and update the visible count
  const handleTabChange = (index) => {
    setActiveTabIndex(index);
    if (index === 1) {
      setVisibleCount(10); // Set to 10 for Table View
      setPageSize(10); // Update page size for Table View
    } else {
      setVisibleCount(6); // Set to 6 for Card View
      setPageSize(6); // Update page size for Card View
    }
    setCurrentPage(1); // Reset to first page when changing tabs
  };

  // Page resets are now handled by smart filter handlers and individual change handlers

  // Show loading state if data is not ready
  if (!data || !Array.isArray(data)) {
    return (
      <div id="researchCentre">
        <div className="loading-state">
          <i className="fas fa-spinner fa-spin"></i>
          <span>Loading News Hub...</span>
        </div>
      </div>
    );
  }

  return (
    <div id="researchCentre">
      <Tabs selectedIndex={activeTabIndex} onSelect={handleTabChange}>
        {/* MediaCentre-style filters */}
        <div className="filters-section">
          <div className="filters-bar">
            {/* Single Row: Search, Filters, View Controls, Results Count */}
            <div className="search-and-controls-row">
              <div className="research-search-box">
                <i className="fas fa-search"></i>
                                  <input
                    type="text"
                    placeholder="Search content..."
                  value={searchQuery}
                  onChange={e => {
                    setSearchQuery(e.target.value);
                    setCurrentPage(1);
                    // Dynamic filter options will update automatically via useEffect
                  }}
                />
                {searchQuery && (
                  <i 
                    className="fas fa-times clear-search" 
                    onClick={() => {
                      setSearchQuery('');
                      setCurrentPage(1);
                      // Dynamic filter options will update automatically via useEffect
                    }}
                  />
                )}
              </div>

              {/* Inline Filter Controls */}
              <div className="inline-filter-controls">
                {/* Date Range Filter */}
                {!selectedYear && (
                  <div className="date-range-filter">
                    <button 
                      className={`date-picker-button ${startDate && endDate ? 'has-dates' : ''}`}
                      onClick={() => {
                        setShowDatePicker(!showDatePicker);
                        setTempStartDate(startDate);
                        setTempEndDate(endDate);
                      }}
                    >
                      <i className="fa fa-calendar"></i>
                      <span className="date-range-text">Select Dates</span>
                    </button>
                    {showDatePicker && (
                      <>
                        <div className="research-date-picker-overlay" onClick={() => setShowDatePicker(false)}></div>
                        <div className="research-date-picker-popup">
                        <div className="research-date-picker-calendars">
                          <div className="research-calendar-container">
                            <div className="research-calendar-header">Start Date</div>
                            <DatePicker
                              selected={tempStartDate}
                              onChange={(date) => setTempStartDate(date)}
                              selectsStart
                              startDate={tempStartDate}
                              endDate={tempEndDate}
                              maxDate={new Date()}
                              inline
                              showMonthDropdown
                              showYearDropdown
                              dropdownMode="select"
                              dateFormat="dd/MM/yyyy"
                              calendarClassName="research-custom-calendar"
                              renderCustomHeader={({
                                date,
                                changeYear,
                                changeMonth,
                                decreaseMonth,
                                increaseMonth,
                                prevMonthButtonDisabled,
                                nextMonthButtonDisabled,
                              }) => {
                                const currentDate = new Date();
                                const currentYear = currentDate.getFullYear();
                                const currentMonth = currentDate.getMonth();
                                const isCurrentYear = date.getFullYear() === currentYear;
                                
                                // Get available years (sorted in descending order)
                                const availableYears = [...availableYearsAndMonths.keys()]
                                  .sort((a, b) => b - a)
                                  .filter(year => year <= currentYear);

                                // Get available months for the selected year
                                const availableMonths = availableYearsAndMonths.get(date.getFullYear()) || new Set();
                                
                                // Disable future months in any year after current year
                                const shouldDisableMonth = (monthIndex) => {
                                  const yearToCheck = date.getFullYear();
                                  if (yearToCheck > currentYear) return true;
                                  if (yearToCheck === currentYear && monthIndex > currentMonth) return true;
                                  return !availableMonths.has(monthIndex);
                                };
                                
                                return (
                                <div className="research-custom-header">
                                  <button onClick={decreaseMonth} disabled={prevMonthButtonDisabled}>
                                    {"<"}
                                  </button>
                                  <select
                                    value={date.getFullYear()}
                                    onChange={({ target: { value } }) => {
                                      const newYear = parseInt(value);
                                      if (newYear <= currentYear) {
                                        changeYear(newYear);
                                      }
                                    }}
                                  >
                                    {availableYears.map(year => (
                                      <option 
                                        key={year} 
                                        value={year}
                                        disabled={year > currentYear}
                                      >
                                        {year}
                                      </option>
                                    ))}
                                  </select>

                                  <select
                                    value={date.getMonth()}
                                    onChange={({ target: { value } }) => {
                                      const newMonth = parseInt(value);
                                      if (!shouldDisableMonth(newMonth)) {
                                        changeMonth(newMonth);
                                      }
                                    }}
                                  >
                                    {monthNames.map((option, i) => (
                                      <option 
                                        key={i} 
                                        value={i}
                                        disabled={shouldDisableMonth(i)}
                                      >
                                        {option}
                                      </option>
                                    ))}
                                  </select>

                                  <button 
                                    onClick={increaseMonth} 
                                    disabled={nextMonthButtonDisabled || shouldDisableMonth(date.getMonth() + 1)}
                                  >
                                    {">"}
                                  </button>
                                </div>
                              )}}
                            />
                            <div className="research-calendar-footer">
                              {tempStartDate ? formatDate(tempStartDate) : "No date selected"}
                            </div>
                          </div>
                          <div className="research-calendar-container">
                            <div className="research-calendar-header">End Date</div>
                            <DatePicker
                              selected={tempEndDate}
                              onChange={(date) => setTempEndDate(date)}
                              selectsEnd
                              startDate={tempStartDate}
                              endDate={tempEndDate}
                              minDate={tempStartDate}
                              maxDate={new Date()}
                              inline
                              showMonthDropdown
                              showYearDropdown
                              dropdownMode="select"
                              dateFormat="dd/MM/yyyy"
                              calendarClassName="research-custom-calendar"
                              renderCustomHeader={({
                                date,
                                changeYear,
                                changeMonth,
                                decreaseMonth,
                                increaseMonth,
                                prevMonthButtonDisabled,
                                nextMonthButtonDisabled,
                              }) => {
                                const currentDate = new Date();
                                const currentYear = currentDate.getFullYear();
                                const currentMonth = currentDate.getMonth();
                                const isCurrentYear = date.getFullYear() === currentYear;
                                
                                // Get available years (sorted in descending order)
                                const availableYears = [...availableYearsAndMonths.keys()]
                                  .sort((a, b) => b - a)
                                  .filter(year => year <= currentYear);

                                // Get available months for the selected year
                                const availableMonths = availableYearsAndMonths.get(date.getFullYear()) || new Set();
                                
                                // Disable future months in any year after current year
                                const shouldDisableMonth = (monthIndex) => {
                                  const yearToCheck = date.getFullYear();
                                  if (yearToCheck > currentYear) return true;
                                  if (yearToCheck === currentYear && monthIndex > currentMonth) return true;
                                  return !availableMonths.has(monthIndex);
                                };
                                
                                return (
                                <div className="research-custom-header">
                                  <button onClick={decreaseMonth} disabled={prevMonthButtonDisabled}>
                                    {"<"}
                                  </button>
                                  <select
                                    value={date.getFullYear()}
                                    onChange={({ target: { value } }) => {
                                      const newYear = parseInt(value);
                                      if (newYear <= currentYear) {
                                        changeYear(newYear);
                                      }
                                    }}
                                  >
                                    {availableYears.map(year => (
                                      <option 
                                        key={year} 
                                        value={year}
                                        disabled={year > currentYear}
                                      >
                                        {year}
                                      </option>
                                    ))}
                                  </select>

                                  <select
                                    value={date.getMonth()}
                                    onChange={({ target: { value } }) => {
                                      const newMonth = parseInt(value);
                                      if (!shouldDisableMonth(newMonth)) {
                                        changeMonth(newMonth);
                                      }
                                    }}
                                  >
                                    {monthNames.map((option, i) => (
                                      <option 
                                        key={i} 
                                        value={i}
                                        disabled={shouldDisableMonth(i)}
                                      >
                                        {option}
                                      </option>
                                    ))}
                                  </select>

                                  <button 
                                    onClick={increaseMonth} 
                                    disabled={nextMonthButtonDisabled || shouldDisableMonth(date.getMonth() + 1)}
                                  >
                                    {">"}
                                  </button>
                                </div>
                              )}}
                            />
                            <div className="research-calendar-footer">
                              {tempEndDate ? formatDate(tempEndDate) : "No date selected"}
                            </div>
                          </div>
                        </div>
                        <div className="research-date-picker-actions">
                          {(tempStartDate || tempEndDate) && (
                            <button 
                              className="research-date-picker-clear" 
                              onClick={() => {
                                setTempStartDate(null);
                                setTempEndDate(null);
                                setStartDate(null);
                                setEndDate(null);
                                setShowDatePicker(false);
                              }}
                            >
                              Clear
                            </button>
                          )}
                          <div className="research-date-picker-actions-right">
                            <button 
                              className="research-date-picker-cancel" 
                              onClick={() => setShowDatePicker(false)}
                            >
                              Cancel
                            </button>
                            <button 
                              className="research-date-picker-apply"
                              onClick={handleApplyDates}
                              disabled={!tempStartDate || !tempEndDate}
                            >
                              Apply
                            </button>
                          </div>
                        </div>
                        </div>
                      </>
                    )}
                  </div>
                )}

                {/* Year Filter - Only show if no dates are selected */}
                {!startDate && !endDate && (
                  <select value={selectedYear} onChange={(e) => {
                    handleSmartFilterChange('year', e.target.value);
                    // Clear date range if year is selected
                    if (e.target.value) {
                      setStartDate(null);
                      setEndDate(null);
                      setShowDatePicker(false);
                    }
                  }}>
                    <option value="">Year (ALL)</option>
                    {uniqueYears.map(year => <option key={year} value={year}>{year}</option>)}
                  </select>
                )}

                {/* Month Filter (only shown if a year is selected) */}
                {selectedYear && (
                  <select value={selectedMonth} onChange={(e) => {
                    handleSmartFilterChange('month', e.target.value);
                  }}>
                    <option value="">All Months</option>
                    {uniqueMonths.map(month => <option key={month} value={month}>{getMonthName(month)}</option>)}
                  </select>
                )}

                {/* Week Filter (only shown if a month is selected) */}
                {selectedMonth && (
                  <select value={selectedWeek} onChange={(e) => handleSmartFilterChange('week', e.target.value)}>
                    <option value="">Week (ALL)</option>
                    {uniqueWeeks.map(week => <option key={week} value={week}>{`Week ${week}`}</option>)}
                  </select>
                )}

                {/* Source Type Filter */}
                <select value={selectedSourceType} onChange={(e) => handleSmartFilterChange('sourceType', e.target.value)}>
                  <option value="">Type (ALL)</option>
                  {uniqueSourceTypes.map(sourceType => (
                    <option key={sourceType} value={sourceType}>
                      {sourceType === 'news' ? 'News' : 'Featured & Submitted Articles'}
                    </option>
                  ))}
                </select>

                {/* Category Filter */}
                <select value={selectedCategory} onChange={(e) => handleSmartFilterChange('category', e.target.value)}>
                  <option value="">Category (ALL)</option>
                  {uniqueCategories.map(category => <option key={category} value={category}>{category}</option>)}
                </select>

                <select
                  id="page-size"
                  value={pageSize}
                  onChange={(e) => handlePageSizeChange(Number(e.target.value))}
                >
                  {activeTabIndex === 0 ? (
                    <>
                      <option value={6}>6 per page</option>
                      <option value={12}>12 per page</option>
                      <option value={18}>18 per page</option>
                      <option value={36}>36 per page</option>
                    </>
                  ) : (
                    <>
                      <option value={10}>10 per page</option>
                      <option value={20}>20 per page</option>
                      <option value={30}>30 per page</option>
                      <option value={50}>50 per page</option>
                    </>
                  )}
                </select>

                              {isFilterActive() && (
                <i className="fa-solid fa-rotate-left resetIcon" title='Reset Filters' onClick={resetFilters}></i>
              )}
            </div>
          </div>
        </div>
        </div>

        {/* Applied Filters Tags & Stats Panel */}
        <div className="research-filters-status">
          <div className="applied-filters-section">
            {getAppliedFilters().length > 0 && (
              <div className="applied-filters">
                <span className="applied-filters-label">Applied filters:</span>
                <div className="filter-tags">
                  {getAppliedFilters().map((filter, index) => (
                    <div key={`${filter.type}-${index}`} className="filter-tag">
                      <span className="filter-tag-label">{filter.label}:</span>
                      <span className="filter-tag-value">{filter.value}</span>
                      <button 
                        className="filter-tag-remove"
                        onClick={filter.onRemove}
                        title={`Remove ${filter.label} filter`}
                      >
                        ×
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
          
          <div className="instant-stats-panel">
            <div className="stats-info">
              <div className="stats-description">
                <i className="fa-solid fa-chart-simple"></i>
                {totalItems === 1 ? '1 item' : `${totalItems} news items`} found
              </div>
              {totalItems < allFilteredData.length && (
                <div className="stats-breakdown">
                  <i className="fas fa-filter"></i>
                  Filtered from {allFilteredData.length.toLocaleString()} total
                </div>
              )}
            </div>
            
            <div className="view-controls">
              <button 
                className={`view-btn ${activeTabIndex === 0 ? 'active' : ''}`}
                onClick={() => handleTabChange(0)}
                title="Card View"
              >
                <i className="fas fa-th"></i>
              </button>
              <button 
                className={`view-btn ${activeTabIndex === 1 ? 'active' : ''}`}
                onClick={() => handleTabChange(1)}
                title="Table View"
              >
                <i className="fas fa-list"></i>
              </button>
            </div>
          </div>
        </div>
        
        {/* Hidden TabList for react-tabs functionality */}
        <TabList style={{ display: 'none' }}>
          <Tab>Card View</Tab>
          <Tab>Table View</Tab>
        </TabList>
        
        <TabPanel>
          {isLoadingPage && (
            <div className="page-loading">
              <i className="fas fa-spinner fa-spin"></i>
              <span>Loading page {loadingPageNumber}...</span>
            </div>
          )}
          <ResearchCentreCards data={filteredData} visibleCount={visibleCount} searchQuery={searchQuery} />
        </TabPanel>
        <TabPanel>
          {isLoadingPage && (
            <div className="page-loading">
              <i className="fas fa-spinner fa-spin"></i>
              <span>Loading page {loadingPageNumber}...</span>
            </div>
          )}
          <ResearchCenterTable data={filteredData} visibleCount={visibleCount} searchQuery={searchQuery} />
        </TabPanel>
        
        {/* Pagination */}
        {totalPages > 1 && !isLoadingPage && (
          <div className="pagination">
            <div className="pagination-info">
              Page {currentPage} of {totalPages}
            </div>
            <div className="pagination-controls">
              <button 
                className="pagination-btn"
                onClick={() => handlePageChange(1)}
                disabled={currentPage === 1}
              >
                <i className="fas fa-angle-double-left"></i>
              </button>
              <button 
                className="pagination-btn"
                onClick={() => handlePageChange(currentPage - 1)}
                disabled={currentPage === 1}
              >
                <i className="fas fa-angle-left"></i>
              </button>
              
              {/* Page numbers */}
              {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                let pageNum;
                if (totalPages <= 5) {
                  pageNum = i + 1;
                } else if (currentPage <= 3) {
                  pageNum = i + 1;
                } else if (currentPage >= totalPages - 2) {
                  pageNum = totalPages - 4 + i;
                } else {
                  pageNum = currentPage - 2 + i;
                }
                
                return (
                  <button
                    key={pageNum}
                    className={`pagination-btn ${pageNum === currentPage ? 'active' : ''}`}
                    onClick={() => handlePageChange(pageNum)}
                  >
                    {pageNum}
                  </button>
                );
              })}
              
              <button 
                className="pagination-btn"
                onClick={() => handlePageChange(currentPage + 1)}
                disabled={currentPage === totalPages}
              >
                <i className="fas fa-angle-right"></i>
              </button>
              <button 
                className="pagination-btn"
                onClick={() => handlePageChange(totalPages)}
                disabled={currentPage === totalPages}
              >
                <i className="fas fa-angle-double-right"></i>
              </button>
            </div>
          </div>
        )}
      </Tabs>
    </div>
  );
};

export default ResearchCentre;
